import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Star, Upload, X, ArrowLeft, Package2 } from 'lucide-react';

export default function WriteReview() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productId = searchParams.get('productId');
  const orderId = searchParams.get('orderId');
  const saleId = searchParams.get('saleId');

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);

  const [reviewData, setReviewData] = useState({
    customer_name: '',
    customer_email: '',
    rating: 0,
    comment: '',
    images: []
  });

  useEffect(() => {
    console.log('Review page loaded with params:', { productId, orderId, saleId });
    if (!productId) {
      toast.error('Invalid review link - missing product ID');
      return;
    }
    fetchProduct();
    fetchCompanySettings();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      console.log('Fetching product with ID:', productId);
      const response = await axios.get(`${API}/public/products/${productId}`);
      console.log('Product fetched successfully:', response.data);
      setProduct(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      console.error('Error response:', error.response?.data);
      console.error('ProductId that failed:', productId);
      toast.error(`Product not found (ID: ${productId})`);
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to load company settings');
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (reviewData.images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload image files only');
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        continue;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setReviewData(prev => ({
          ...prev,
          images: [...prev.images, e.target.result]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setReviewData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (reviewData.rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!reviewData.comment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        order_id: orderId,
        sale_id: saleId,
        customer_name: reviewData.customer_name,
        customer_email: reviewData.customer_email,
        rating: reviewData.rating,
        comment: reviewData.comment,
        images: reviewData.images
      });

      toast.success('Review submitted successfully!');
      
      // Redirect to product detail page after 2 seconds
      setTimeout(() => {
        navigate(`/product/${productId}`);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Product Not Found</h2>
          <Button onClick={() => navigate('/')}>Go to Homepage</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-2">
              {companySettings?.website_logo ? (
                <img src={companySettings.website_logo} alt="Logo" className="w-10 h-10 object-contain" />
              ) : (
                <Package2 className="w-8 h-8 text-emerald-600" />
              )}
              <h1 className="text-2xl font-bold text-slate-900">{companySettings?.company_name || 'AN-NISA Nexus'}</h1>
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(`/product/${productId}`)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Product
        </Button>

        <Card className="p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Write a Review</h1>
          <p className="text-slate-600 mb-6">Share your experience with this product</p>

          {/* Product Info */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg mb-8">
            {product.images && product.images[0] && (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-20 h-20 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">{product.name}</h3>
              <p className="text-sm text-slate-600">{product.category}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Name */}
            <div>
              <Label>Your Name *</Label>
              <Input
                value={reviewData.customer_name}
                onChange={(e) => setReviewData({ ...reviewData, customer_name: e.target.value })}
                required
                placeholder="Enter your name"
              />
            </div>

            {/* Customer Email */}
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={reviewData.customer_email}
                onChange={(e) => setReviewData({ ...reviewData, customer_email: e.target.value })}
                placeholder="your.email@example.com"
              />
            </div>

            {/* Rating */}
            <div>
              <Label>Rating *</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= reviewData.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-slate-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {reviewData.rating > 0 && (
                <p className="text-sm text-slate-600 mt-2">
                  {reviewData.rating === 1 && 'Poor'}
                  {reviewData.rating === 2 && 'Fair'}
                  {reviewData.rating === 3 && 'Good'}
                  {reviewData.rating === 4 && 'Very Good'}
                  {reviewData.rating === 5 && 'Excellent'}
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <Label>Your Review *</Label>
              <textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                required
                rows={6}
                placeholder="Tell us about your experience with this product..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Image Upload */}
            <div>
              <Label>Add Photos (Optional - Max 3)</Label>
              <p className="text-sm text-slate-600 mb-3">Maximum 5MB per image</p>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                {reviewData.images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {reviewData.images.length < 3 && (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('image-upload').click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Images
                    </Button>
                  </label>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
